"use client";

import { useRef, useState } from "react";
import { I18nProvider } from "@lingui/react";
import { setupI18n, type Messages } from "@lingui/core";

export function LinguiClientProvider({
  locale,
  messages,
  children,
  sourceLocale,
  pseudoLocale,
}: {
  locale: string;
  messages: Messages;
  children: React.ReactNode;
  /** Source locale to activate for Intl formatting when `locale` is the pseudo locale. */
  sourceLocale?: string;
  /** The pseudo locale identifier (e.g. "x-pseudo"); when `locale` equals it, `sourceLocale` is activated instead. */
  pseudoLocale?: string;
}) {
  // x-pseudo is not a valid BCP-47 tag; activating it makes Lingui's Intl-backed
  // plural/number/date formatting throw RangeError on the client (mirrors the
  // server helper). Activate the source locale for formatting while still keying
  // the pseudo messages under it, so lookups resolve and formatting is safe.
  const intlLocale =
    pseudoLocale != null && sourceLocale != null && locale === pseudoLocale
      ? sourceLocale
      : locale;

  // useState initializer runs once per React component instance. In SSR each
  // request gets its own React tree, so each request gets its own I18n instance —
  // avoiding locale bleed across concurrent SSR requests that the module-level
  // singleton `i18n` from @lingui/core would cause.
  const [i18n] = useState(() =>
    setupI18n({ locale: intlLocale, messages: { [intlLocale]: messages } }),
  );

  // Keep in sync if the props change after mount. Track the *logical* locale,
  // not i18n.locale: source and pseudo collapse to the same intlLocale, so a
  // source<->pseudo toggle leaves i18n.locale unchanged and would otherwise
  // skip the catalog reload — rendering the wrong translations.
  const loadedLocale = useRef(locale);
  if (loadedLocale.current !== locale || i18n.locale !== intlLocale) {
    i18n.load(intlLocale, messages);
    i18n.activate(intlLocale);
    loadedLocale.current = locale;
  }

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
